import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useSettings } from '../contexts/SettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { logoImage } from '../utils/imageOptimizer';

const LanguageSwitcher = () => {
  const { language, setLanguage } = useLanguage();
  const { settings } = useSettings();
  
  const allLanguages = [
    { code: 'en', label: 'EN', flag: '🇬🇧' },
    { code: 'tr', label: 'TR', flag: '🇹🇷' },
    { code: 'id', label: 'ID', flag: '🇮🇩' }
  ];

  const enabledCodes = (settings.enabledLanguages || 'en,tr,id').split(',').map(l => l.trim());
  const languages = allLanguages.filter(l => enabledCodes.includes(l.code));

  if (languages.length <= 1) return null;

  return (
    <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
      {languages.map((lang) => (
        <button
          key={lang.code}
          onClick={() => setLanguage(lang.code)}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            language === lang.code
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <span className="mr-1">{lang.flag}</span>
          {lang.label}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { settings } = useSettings();
  const { t, getBilingualValue } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const primaryColor = settings.colors?.primary || '#0E7A5F';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (id === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(id);
      if (element) {
        const offset = 80;
        const elementPosition = element.offsetTop - offset;
        window.scrollTo({ top: elementPosition, behavior: 'smooth' });
      }
    }
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { label: getBilingualValue(settings.nav?.link1?.text) || t('nav.about'), id: 'about' },
    { label: getBilingualValue(settings.nav?.link2?.text) || t('nav.gallery'), id: 'gallery' },
    { label: getBilingualValue(settings.nav?.link3?.text) || t('nav.packages'), id: 'packages' },
    { label: getBilingualValue(settings.nav?.link4?.text) || t('nav.contact'), id: 'contact' }
  ];

  return (
    <header
      className={`fixed top-0 w-full z-50 border-b border-gray-200 bg-white/95 backdrop-blur-md transition-all duration-300 ${
        scrolled ? 'py-2' : 'py-4'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <button
            onClick={() => scrollToSection('top')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            {settings.nav?.logoImage ? (
              <img 
                src={logoImage(settings.nav.logoImage)} 
                alt={settings.nav?.logoText || 'Logo'} 
                className="h-10 w-auto object-contain"
              />
            ) : (
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: primaryColor }}
              >
                {(settings.nav?.logoText || 'ABF')[0]}
              </div>
            )}
            <span className="text-xl font-bold text-gray-800">
              {getBilingualValue(settings.nav?.logoText) || 'ASEAN Business Forum'}
            </span>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-gray-700 hover:text-green-800 transition-colors font-medium"
              >
                {link.label}
              </button>
            ))}
            
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            <Button
              size="sm"
              onClick={() => scrollToSection('exhibitor')}
              className="text-white"
              style={{ backgroundColor: primaryColor }}
            >
              {getBilingualValue(settings.nav?.ctaText) || t('nav.registerNow')}
            </Button>
          </nav>

          {/* Mobile Menu Toggle */}
          <div className="lg:hidden flex items-center gap-4">
            <LanguageSwitcher />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-700 hover:text-green-800 transition-colors"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4 space-y-4">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="block w-full text-left text-gray-700 hover:text-green-800 transition-colors font-medium"
              >
                {link.label}
              </button>
            ))}
            <div className="pt-2">
              <Button
                size="sm"
                onClick={() => scrollToSection('exhibitor')}
                className="text-white w-full"
                style={{ backgroundColor: primaryColor }}
              >
                {getBilingualValue(settings.nav?.ctaText) || t('nav.registerNow')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
